/**
 * Phase 27.49 — Settings → Integrations → QuickBooks (KR SuperAdmin only).
 *
 * OAuth 2.0 Authorization Code flow with Intuit for BOTH Accounting and
 * Payments Data Sources. This is Phase 1 — only connect / status /
 * disconnect. Payments checkout arrives in later phases.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, RefreshCw, Loader2, CheckCircle2, XCircle, ExternalLink,
  Shield, Building2, KeyRound, Info, CreditCard, ChevronDown, ChevronRight, Copy, Check,
  Activity, AlertTriangle, Clock, Zap,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { quickbooksAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function QuickBooksIntegrationPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await quickbooksAPI.status();
      setStatus(r.data);
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to load QuickBooks status'));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle bounce-back from the OAuth callback (?quickbooks=connected|denied|error).
  useEffect(() => {
    const result = params.get('quickbooks');
    if (!result) return;
    if (result === 'connected') toast.success('QuickBooks connected');
    else if (result === 'denied') toast.info('QuickBooks authorization was cancelled');
    else toast.error('QuickBooks connection failed');
  }, [params]);

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await quickbooksAPI.authUrl();
      window.location.assign(r.data.authorize_url);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to start QuickBooks OAuth'));
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    setDisconnecting(true);
    try {
      const r = await quickbooksAPI.disconnect();
      toast.success(r.data?.already ? 'QuickBooks was not connected' : 'QuickBooks disconnected');
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to disconnect QuickBooks'));
    } finally { setDisconnecting(false); }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 flex items-center gap-2 text-slate-500">
        <Loader2 size={16} className="animate-spin" /> Loading QuickBooks status…
      </div>
    );
  }

  const connected = !!status?.connected;
  const env = status?.environment || 'sandbox';

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="qb-integration-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-slate-500 hover:text-slate-800 text-sm flex items-center gap-1 mb-1"
            data-testid="qb-back-btn"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <h1 className="text-2xl font-heading text-slate-900">QuickBooks Online</h1>
          <p className="text-sm text-slate-500 mt-1">
            OAuth connection for Accounting + Payments. Required for Marketing Materials online checkout.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          data-testid="qb-refresh-btn"
        >
          <RefreshCw size={14} className="mr-1.5" /> Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 text-rose-800 p-4 text-sm" data-testid="qb-error">
          {error}
        </div>
      )}

      {/* Connection status */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Shield size={16} className="text-slate-500" />
              Connection status
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className={env === 'production'
                  ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                  : 'bg-amber-50 text-amber-800 border-amber-300'}
                data-testid="qb-env-badge"
              >
                {env === 'production' ? 'Production' : 'Sandbox'}
              </Badge>
              <Badge
                variant="outline"
                className={connected
                  ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                  : 'bg-slate-100 text-slate-600 border-slate-300'}
                data-testid="qb-conn-badge"
              >
                {connected
                  ? <CheckCircle2 size={11} className="mr-1" />
                  : <XCircle size={11} className="mr-1" />}
                {connected ? 'Connected' : 'Not connected'}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
            <Row label="Realm ID (Company)" value={status?.realm_id || '—'} icon={<Building2 size={12} />} testid="qb-realm-id" />
            <Row label="Environment" value={env} icon={<Info size={12} />} />
            <Row label="Connected at" value={fmtDate(status?.connected_at)} />
            <Row label="Last refreshed" value={fmtDate(status?.last_refreshed_at)} />
            <Row label="Access token expires" value={fmtDate(status?.access_token_expires_at)} />
            <Row label="Refresh token expires" value={fmtDate(status?.refresh_token_expires_at)} />
            <Row
              label="Scopes"
              value={status?.scopes || 'com.intuit.quickbooks.accounting com.intuit.quickbooks.payment'}
              full
            />
            <Row
              label="Redirect URI"
              value={status?.redirect_uri || '—'}
              icon={<KeyRound size={12} />}
              full
              mono
            />
          </div>

          <div className="pt-3 border-t border-slate-100 flex flex-wrap gap-2">
            {!connected ? (
              <Button
                onClick={connect}
                disabled={connecting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="qb-connect-btn"
              >
                {connecting && <Loader2 size={14} className="mr-1.5 animate-spin" />}
                <ExternalLink size={14} className="mr-1.5" />
                Connect QuickBooks
              </Button>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" data-testid="qb-disconnect-btn">
                    Disconnect
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Disconnect QuickBooks?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This revokes the connection and clears the stored tokens. Any Marketing
                      Materials checkout will stop working until a new admin reconnects.
                      Historical Sales Receipts already synced to QuickBooks remain unaffected.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={disconnecting}>Keep connected</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={(e) => { e.preventDefault(); disconnect(); }}
                      disabled={disconnecting}
                      className="bg-rose-600 hover:bg-rose-700 text-white"
                      data-testid="qb-disconnect-confirm"
                    >
                      {disconnecting && <Loader2 size={14} className="mr-1.5 animate-spin" />}
                      Disconnect
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Payment pipeline monitoring — always shown so ops can review
          historical qb_charges/qb_refunds even if the OAuth link is currently down. */}
      <PaymentPipelineMonitoring />

      {/* Setup checklist */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Setup checklist</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-600 space-y-2">
          <p>Before clicking <em>Connect</em>, confirm the following in the Intuit Developer Portal for this app:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Both <strong>Data Sources</strong> enabled: <code>Accounting</code> and <code>Payments</code>.</li>
            <li>
              Under <strong>Development → Keys &amp; OAuth Redirect URIs</strong>, add exactly
              this URL (this environment is <strong>{env}</strong>):
              <div className="mt-1 rounded bg-slate-900 text-slate-100 font-mono text-[11px] px-2 py-1 break-all">
                {status?.redirect_uri || '—'}
              </div>
            </li>
            <li>A <strong>Sandbox Company</strong> is created under the Intuit Developer Dashboard.</li>
            <li>The <strong>Intuit Payments</strong> merchant account is active on that Sandbox Company.</li>
          </ul>
        </CardContent>
      </Card>

      {/* Sandbox Test Cards Panel — hidden in production */}
      {env !== 'production' && <SandboxTestCardsPanel />}
    </div>
  );
}

// ─── Payment Pipeline Monitoring ────────────────────────────────────
// Phase 27.49.P8 — Live counts + stuck-order list so ops can spot
// Payment_Unknown timeouts and receipt-sync stalls at a glance.
const HEALTH_BADGE = {
  healthy:  { label: 'Healthy',  cls: 'bg-emerald-50 text-emerald-800 border-emerald-300', Icon: CheckCircle2 },
  degraded: { label: 'Degraded', cls: 'bg-amber-50 text-amber-800 border-amber-300',       Icon: AlertTriangle },
  stalled:  { label: 'Stalled',  cls: 'bg-rose-50 text-rose-800 border-rose-300',          Icon: AlertTriangle },
};

function fmtAge(seconds) {
  if (!seconds || seconds < 60) return `${seconds || 0}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

function Metric({ label, value, tone = 'slate', testid }) {
  const toneCls = {
    slate:   'bg-slate-50 border-slate-200 text-slate-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber:   'bg-amber-50 border-amber-200 text-amber-800',
    rose:    'bg-rose-50 border-rose-200 text-rose-800',
    blue:    'bg-blue-50 border-blue-200 text-blue-800',
  }[tone];
  return (
    <div className={`rounded-md border px-3 py-2.5 ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-2xl font-bold leading-none mt-1">{value ?? 0}</div>
    </div>
  );
}

function PaymentPipelineMonitoring() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async ({ silent } = {}) => {
    if (silent) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const r = await quickbooksAPI.monitoring();
      setData(r.data);
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to load monitoring metrics'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s to keep stuck-count fresh without hammering.
  useEffect(() => {
    const t = setInterval(() => load({ silent: true }), 30_000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-slate-500 flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading monitoring data…
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded" data-testid="qb-monitoring-error">
          {error}
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const health = HEALTH_BADGE[data.reconciler?.health || 'healthy'];
  const stuckCount = data.charges?.stuck_unknown_count || 0;
  const manualCount = data.sales_receipts?.manual_pending?.length || 0;

  return (
    <Card data-testid="qb-monitoring-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Activity size={16} className="text-slate-500" />
            Payment pipeline monitoring
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={health.cls}
              data-testid="qb-reconciler-health"
            >
              <health.Icon size={11} className="mr-1" />
              Reconciler: {health.label}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={() => load({ silent: true })}
              disabled={refreshing}
              data-testid="qb-monitoring-refresh"
            >
              {refreshing
                ? <Loader2 size={13} className="mr-1.5 animate-spin" />
                : <RefreshCw size={13} className="mr-1.5" />}
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6 text-sm">
        {/* Charges */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1">
            <Zap size={11} /> Charges (last 24h + all-time)
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Metric label="Captured"       value={data.charges.counts.CAPTURED}          tone="emerald" testid="qb-metric-captured" />
            <Metric label="Unknown (stuck)" value={data.charges.counts.UNKNOWN}          tone={stuckCount > 0 ? 'amber' : 'slate'} testid="qb-metric-unknown" />
            <Metric label="Declined"       value={data.charges.counts.DECLINED}          tone="slate"   testid="qb-metric-declined" />
            <Metric label="Reauth Req'd"   value={data.charges.counts.REAUTH_REQUIRED}   tone="slate"   testid="qb-metric-reauth" />
          </div>
        </div>

        {/* Sales Receipts */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1">
            <Building2 size={11} /> Sales Receipt sync
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Metric label="Synced"          value={data.sales_receipts.counts.synced}                    tone="emerald" testid="qb-metric-sr-synced" />
            <Metric label="Pending sync"    value={data.sales_receipts.counts.charge_ok_receipt_pending} tone={data.sales_receipts.counts.charge_ok_receipt_pending > 0 ? 'blue' : 'slate'} testid="qb-metric-sr-pending" />
            <Metric label="Manual required" value={data.sales_receipts.counts.receipt_manual_required}   tone={manualCount > 0 ? 'rose' : 'slate'} testid="qb-metric-sr-manual" />
          </div>
        </div>

        {/* Refunds */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1">
            <RefreshCw size={11} /> Refunds
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Metric label="Issued"  value={data.refunds.counts.ISSUED}  tone="emerald" testid="qb-metric-ref-issued" />
            <Metric label="Pending" value={data.refunds.counts.PENDING} tone="blue"    testid="qb-metric-ref-pending" />
            <Metric label="Failed"  value={data.refunds.counts.FAILED}  tone={data.refunds.counts.FAILED > 0 ? 'rose' : 'slate'} testid="qb-metric-ref-failed" />
          </div>
        </div>

        {/* Stuck UNKNOWN table */}
        {stuckCount > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold mb-2 flex items-center gap-1">
              <Clock size={11} /> Stuck UNKNOWN charges — reconciler is retrying
            </div>
            <div className="overflow-x-auto rounded-md border border-amber-200">
              <table className="w-full text-xs" data-testid="qb-stuck-unknowns-table">
                <thead className="bg-amber-50 text-amber-900">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Order</th>
                    <th className="text-left px-3 py-2 font-semibold">Amount</th>
                    <th className="text-left px-3 py-2 font-semibold">Age</th>
                    <th className="text-left px-3 py-2 font-semibold">Attempts</th>
                    <th className="text-left px-3 py-2 font-semibold">Last error</th>
                  </tr>
                </thead>
                <tbody>
                  {data.charges.stuck_unknowns.map((s, i) => (
                    <tr key={s.intent_id} className={i % 2 ? 'bg-amber-50/40' : ''}>
                      <td className="px-3 py-2 font-mono text-slate-800 break-all">{s.order_id}</td>
                      <td className="px-3 py-2 font-mono text-slate-800">${Number(s.amount || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-slate-700">{fmtAge(s.age_seconds)}</td>
                      <td className="px-3 py-2 text-slate-700">{s.attempts}</td>
                      <td className="px-3 py-2 text-slate-600 max-w-[280px] truncate" title={s.last_error || ''}>{s.last_error || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Oldest stuck row: <strong>{fmtAge(data.charges.oldest_unknown_age_seconds)}</strong> — Intuit token TTL is 15m before the reconciler gives up.</p>
          </div>
        )}

        {/* Manual-required receipts */}
        {manualCount > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-rose-700 font-semibold mb-2 flex items-center gap-1">
              <AlertTriangle size={11} /> Sales Receipts needing manual sync
            </div>
            <div className="overflow-x-auto rounded-md border border-rose-200">
              <table className="w-full text-xs" data-testid="qb-manual-receipts-table">
                <thead className="bg-rose-50 text-rose-900">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Order</th>
                    <th className="text-left px-3 py-2 font-semibold">Requester</th>
                    <th className="text-left px-3 py-2 font-semibold">Amount</th>
                    <th className="text-left px-3 py-2 font-semibold">QB Charge</th>
                    <th className="text-left px-3 py-2 font-semibold">Paid at</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sales_receipts.manual_pending.map((m, i) => (
                    <tr key={m.order_id} className={i % 2 ? 'bg-rose-50/40' : ''}>
                      <td className="px-3 py-2 font-mono text-slate-800 break-all">{m.order_id}</td>
                      <td className="px-3 py-2 text-slate-700">{m.requester_name || m.requester_email || '—'}</td>
                      <td className="px-3 py-2 font-mono text-slate-800">${Number(m.total_amount || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 font-mono text-slate-600">{m.qb_charge_id || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{m.paid_at ? new Date(m.paid_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Reconciler footer */}
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="qb-reconciler-summary">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <strong>Reconciler activity (last 24h):</strong>{' '}
              {data.reconciler.activity_24h.unknown_charge_resolved || 0} unknowns resolved,{' '}
              {data.reconciler.activity_24h.unknown_charge_gave_up || 0} given up,{' '}
              {data.reconciler.activity_24h.sales_receipt_created_by_reconciler || 0} receipts synced,{' '}
              {data.reconciler.activity_24h.sales_receipt_gave_up || 0} marked manual.
            </div>
            {data.reconciler.last_activity && (
              <div className="text-slate-500">
                Last event: <span className="font-mono">{data.reconciler.last_activity.action}</span>{' '}
                @ {new Date(data.reconciler.last_activity.created_at).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Sandbox Test Cards Panel ───────────────────────────────────────
// Official Intuit sandbox test data. The sandbox does NOT validate
// AVS/CVV/limits — it matches on card number + expiration + amount +
// name-based emulation tags. Docs:
//   https://developer.intuit.com/app/developer/qbpayments/docs/workflows/test-your-app
const SANDBOX_APPROVED_CARDS = [
  { brand: 'Visa',       number: '4112344112344113',    cvv: '123', exp: '12/29' },
  { brand: 'MasterCard', number: '5111005111051128',    cvv: '123', exp: '12/29' },
  { brand: 'Amex',       number: '378282246310005',     cvv: '1234', exp: '12/29' },
  { brand: 'Discover',   number: '6011000995500000',    cvv: '123', exp: '12/29' },
  { brand: 'Diners',     number: '36438999960016',      cvv: '123', exp: '12/29' },
  { brand: 'JCB',        number: '3530111333300000',    cvv: '123', exp: '12/29' },
];

const SANDBOX_AMOUNT_EMULATIONS = [
  { amount: '$1.11',  result: 'PENDING',   note: 'Charge stays in Payment_Unknown until reconciler polls' },
  { amount: '$2.22',  result: 'PENDING',   note: 'Alternate pending trigger' },
  { amount: '$3.33',  result: 'DECLINED',  note: 'Simulate declined transaction' },
  { amount: '$4.44',  result: 'DECLINED',  note: 'Alternate declined trigger' },
  { amount: '$5.55',  result: 'SUCCEEDED', note: 'Force success regardless of card' },
];

const SANDBOX_NAME_EMULATIONS = [
  { name: 'emulate=10201', result: 'Payment system error',  note: 'Simulate generic processor error' },
  { name: 'emulate=10301', result: 'Invalid card number',   note: 'Simulate PMT-4000 invalid card' },
  { name: 'emulate=10401', result: 'General decline',       note: 'Simulate cardholder bank decline' },
];

function SandboxTestCardsPanel() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState('');

  const copy = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      toast.success(`Copied ${value}`);
      setTimeout(() => setCopied(''), 1500);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <Card data-testid="qb-test-cards-panel">
      <CardHeader className="pb-3">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center justify-between text-left"
          data-testid="qb-test-cards-toggle"
        >
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <CreditCard size={16} className="text-slate-500" />
            Sandbox Test Cards
            <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-800 border-amber-300 text-[10px]">
              QA Only
            </Badge>
          </CardTitle>
          {open
            ? <ChevronDown size={16} className="text-slate-500" />
            : <ChevronRight size={16} className="text-slate-500" />}
        </button>
      </CardHeader>
      {open && (
        <CardContent className="space-y-6 text-sm">
          <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 p-3 text-xs" data-testid="qb-test-cards-warning">
            <strong>Reminder:</strong> These values only work against the Intuit Sandbox
            environment. The sandbox emulator does <em>not</em> validate AVS, CVV, or
            available balance — it matches on card number, expiration, amount, and the
            name-on-card emulation strings below.
          </div>

          {/* Approved cards */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">
              1. Approved cards (Charge → CAPTURED)
            </div>
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="w-full text-xs" data-testid="qb-approved-cards-table">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Brand</th>
                    <th className="text-left px-3 py-2 font-semibold">Card Number</th>
                    <th className="text-left px-3 py-2 font-semibold">CVV</th>
                    <th className="text-left px-3 py-2 font-semibold">Expiration</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {SANDBOX_APPROVED_CARDS.map((c, i) => (
                    <tr key={c.number} className={i % 2 ? 'bg-slate-50/50' : ''}>
                      <td className="px-3 py-2 font-medium text-slate-800">{c.brand}</td>
                      <td className="px-3 py-2 font-mono text-slate-800">{c.number}</td>
                      <td className="px-3 py-2 font-mono text-slate-600">{c.cvv}</td>
                      <td className="px-3 py-2 font-mono text-slate-600">{c.exp}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => copy(c.number, `card-${c.number}`)}
                          className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 text-[11px] px-2 py-1 rounded hover:bg-slate-100"
                          data-testid={`qb-copy-card-${c.brand.toLowerCase()}`}
                        >
                          {copied === `card-${c.number}`
                            ? <><Check size={11} /> Copied</>
                            : <><Copy size={11} /> Copy</>}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Amount-based emulation */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">
              2. Amount-based scenarios (use any approved card + these totals)
            </div>
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="w-full text-xs" data-testid="qb-amount-emulation-table">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Amount</th>
                    <th className="text-left px-3 py-2 font-semibold">Expected Result</th>
                    <th className="text-left px-3 py-2 font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {SANDBOX_AMOUNT_EMULATIONS.map((a, i) => (
                    <tr key={a.amount} className={i % 2 ? 'bg-slate-50/50' : ''}>
                      <td className="px-3 py-2 font-mono text-slate-800">{a.amount}</td>
                      <td className="px-3 py-2">
                        <Badge
                          variant="outline"
                          className={
                            a.result === 'SUCCEEDED'
                              ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                              : a.result === 'DECLINED'
                                ? 'bg-rose-50 text-rose-800 border-rose-300'
                                : 'bg-slate-100 text-slate-700 border-slate-300'
                          }
                        >
                          {a.result}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{a.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Name-based emulation */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">
              3. Name-on-card emulation tags (paste into the cardholder name field)
            </div>
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="w-full text-xs" data-testid="qb-name-emulation-table">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Name-on-Card</th>
                    <th className="text-left px-3 py-2 font-semibold">Expected Result</th>
                    <th className="text-left px-3 py-2 font-semibold">Notes</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {SANDBOX_NAME_EMULATIONS.map((n, i) => (
                    <tr key={n.name} className={i % 2 ? 'bg-slate-50/50' : ''}>
                      <td className="px-3 py-2 font-mono text-slate-800">{n.name}</td>
                      <td className="px-3 py-2 text-slate-700">{n.result}</td>
                      <td className="px-3 py-2 text-slate-600">{n.note}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => copy(n.name, `name-${n.name}`)}
                          className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 text-[11px] px-2 py-1 rounded hover:bg-slate-100"
                          data-testid={`qb-copy-name-${n.name}`}
                        >
                          {copied === `name-${n.name}`
                            ? <><Check size={11} /> Copied</>
                            : <><Copy size={11} /> Copy</>}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes on AVS / CVV mismatches */}
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 space-y-1" data-testid="qb-avs-cvv-note">
            <p><strong>AVS / CVV mismatch:</strong> The sandbox emulator does not validate
            AVS or CVV values, so a real mismatch flow can only be exercised against a
            production QuickBooks trial company with fraud preferences enabled. In
            production the receipt will surface the corresponding <code>PMT-2000</code>
            (CVC) or <code>PMT-2002</code> (address) error codes.</p>
            <p>
              <a
                href="https://developer.intuit.com/app/developer/qbpayments/docs/workflows/test-your-app"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline inline-flex items-center gap-1"
                data-testid="qb-test-cards-docs-link"
              >
                Full Intuit sandbox testing docs <ExternalLink size={11} />
              </a>
            </p>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function Row({ label, value, icon, full, mono, testid }) {
  return (
    <div className={full ? 'md:col-span-2' : ''}>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1">
        {icon} {label}
      </div>
      <div
        className={`${mono ? 'font-mono text-xs' : 'text-sm'} text-slate-800 break-all`}
        data-testid={testid}
      >
        {value}
      </div>
    </div>
  );
}
